import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Megaphone, MousePointerClick, Mail, BadgeCheck, Loader2, History } from "lucide-react";
import { toast } from "sonner";

function firstOfMonthIso() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

function jumpToRedemptionsLog() {
  window.dispatchEvent(new CustomEvent("open-redemptions-log", { detail: { preset: "thismonth" } }));
}

export default function MarketingReportCard() {
  const [data, setData] = useState({ items: [], totals: null });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [monthKpi, setMonthKpi] = useState({ count: 0, promo: 0, loyalty: 0, loading: true });
  const monthStart = useMemo(() => firstOfMonthIso(), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/me/business/marketing-report");
      setData(payload);
    } catch (e) {
      console.warn("marketing report failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMonthKpi = useCallback(async () => {
    setMonthKpi((k) => ({ ...k, loading: true }));
    try {
      const { data: r } = await api.get("/me/business/redemptions", { params: { date_from: monthStart, limit: 500 } });
      const t = r.totals || {};
      setMonthKpi({ count: t.count || 0, promo: t.promo || 0, loyalty: t.loyalty || 0, loading: false });
    } catch (e) {
      setMonthKpi({ count: 0, promo: 0, loyalty: 0, loading: false });
    }
  }, [monthStart]);

  useEffect(() => { load(); loadMonthKpi(); }, [load, loadMonthKpi]);

  const markRedeemed = async (pid) => {
    setBusyId(pid);
    try {
      const { data: r } = await api.post(`/me/business/promotions/${pid}/mark-redeemed`);
      toast.success(`Logged redemption · total ${r.redeemed_total}`);
      load();
      loadMonthKpi();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const t = data.totals;

  return (
    <Card className="rounded-md border border-border p-6" data-testid="marketing-report-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Megaphone size={18} className="text-[#B31E1E]" />
        <h3 className="font-display text-2xl">Marketing ROI</h3>
        <Badge variant="outline" className="rounded-sm text-[10px] font-mono">{data.items?.length || 0} promo{(data.items?.length || 0) === 1 ? "" : "s"}</Badge>
      </div>
      <p className="text-sm text-gray-500 mt-1 max-w-2xl">
        Track how your promotional message blasts perform — recipients sent, link clicks, and coupon redemptions you've marked in-store. Resend webhook opens appear here too when configured.
      </p>

      {/* This-month KPI tile — clickable jump into the full audit log */}
      <button
        type="button"
        onClick={jumpToRedemptionsLog}
        className="mt-5 w-full text-left group focus:outline-none focus:ring-2 focus:ring-[#B31E1E]/40 rounded-md"
        data-testid="marketing-month-kpi"
        title="Open the Redemptions log filtered to this month"
      >
        <div className="rounded-md border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-4 flex items-center justify-between gap-3 group-hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <History size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">Redemptions this month</div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="font-display text-3xl tabular-nums text-emerald-900" data-testid="marketing-month-kpi-count">
                  {monthKpi.loading ? "—" : monthKpi.count}
                </span>
                <div className="flex gap-1.5">
                  <span className="rounded-full bg-white border border-[#B31E1E]/30 text-[#B31E1E] text-[10px] font-mono px-2 py-0.5" data-testid="marketing-month-kpi-promo">
                    {monthKpi.promo} promo
                  </span>
                  <span className="rounded-full bg-white border border-emerald-300 text-emerald-800 text-[10px] font-mono px-2 py-0.5" data-testid="marketing-month-kpi-loyalty">
                    {monthKpi.loyalty} loyalty
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Every coupon + loyalty reward you&apos;ve marked as redeemed since {monthStart}. Tap to open the full audit log →
              </p>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 text-xs font-mono text-[#B31E1E] group-hover:underline shrink-0">
            View log →
          </span>
        </div>
      </button>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="marketing-totals">
        <Stat icon={<Mail size={13} />} label="Recipients" value={t?.recipients ?? 0} testId="marketing-total-recipients" />
        <Stat icon={<Mail size={13} />} label="Delivered" value={t?.delivered ?? 0} testId="marketing-total-delivered" />
        <Stat icon={<Mail size={13} />} label="Opens" value={t?.opens ?? 0} testId="marketing-total-opens" />
        <Stat icon={<MousePointerClick size={13} />} label="Clicks" value={t?.clicks ?? 0} testId="marketing-total-clicks" highlight />
        <Stat icon={<BadgeCheck size={13} />} label="Redemptions" value={t?.redemptions ?? 0} testId="marketing-total-redemptions" emerald />
      </div>

      {loading && data.items.length === 0 ? (
        <p className="text-sm text-gray-500 mt-5">Loading…</p>
      ) : data.items.length === 0 ? (
        <div className="mt-5 border border-dashed border-border rounded-md p-5 text-sm text-gray-600" data-testid="marketing-empty">
          You haven't sent any promotional blasts yet. Send one from the Promotions card above to start tracking.
        </div>
      ) : (
        <div className="mt-5 border border-border rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="marketing-promo-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Promotion</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Coupon</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Sent</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Opens</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Clicks</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">CTR</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Redemptions</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.items.map((r) => (
                <tr key={r.promo_id} data-testid={`marketing-row-${r.promo_id}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900 max-w-[260px] truncate" title={r.title}>{r.title}</td>
                  <td className="px-3 py-2">
                    {r.discount_code ? (
                      <span className="font-mono text-[11px] bg-fuchsia-50 border border-dashed border-fuchsia-300 text-fuchsia-700 px-2 py-0.5 rounded">
                        {r.discount_code}
                      </span>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right">{r.recipients}</td>
                  <td className="px-3 py-2 tabular-nums text-right text-gray-600">{r.opens}</td>
                  <td className="px-3 py-2 tabular-nums text-right">
                    {r.clicks > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-semibold">
                        <MousePointerClick size={10} /> {r.clicks}
                      </span>
                    ) : <span className="text-gray-400">0</span>}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right text-[#B31E1E] font-semibold">
                    {r.click_rate_pct}%
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right">
                    {r.redemptions > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 text-xs font-semibold">
                        <BadgeCheck size={10} /> {r.redemptions}
                      </span>
                    ) : <span className="text-gray-400">0</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-[35px] h-8 text-xs"
                      disabled={busyId === r.promo_id}
                      onClick={() => markRedeemed(r.promo_id)}
                      data-testid={`marketing-mark-redeemed-${r.promo_id}`}
                    >
                      {busyId === r.promo_id ? <Loader2 size={11} className="animate-spin" /> : "+ Mark redeemed"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Stat({ icon, label, value, highlight, emerald, testId }) {
  return (
    <div className="border border-border rounded-md p-3 bg-white" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-gray-500">
        {icon}
        <span className="text-[10px] font-mono uppercase tracking-wider">{label}</span>
      </div>
      <p className={`font-display text-2xl mt-1 tabular-nums ${
        emerald ? "text-emerald-600" : highlight ? "text-[#B31E1E]" : "text-gray-900"
      }`}>
        {value}
      </p>
    </div>
  );
}
